import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Slider } from "../components/ui/slider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Headset,
  Glasses,
  Volume2,
  VolumeX,
  Save,
  Trash2,
  RotateCcw,
  Activity,
  ChevronRight,
  Play,
  Pause,
  Link2,
  Clapperboard,
  X,
  SkipForward,
  ChevronLeft,
  Settings2,
  SlidersHorizontal,
} from "lucide-react";
import { stressStore, useStress, crisisLevel } from "../store";
import { startAudio, setMuted, isMuted } from "../audio";
import { useTour, startTour, stopTour, nextStep, prevStep, TOUR_STEPS } from "./tour";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTROLS = [
  { key: "phd", label: "PhD Production Rate", hint: "Oversupply of new doctorates" },
  { key: "budget", label: "Budget Contraction", hint: "Shrinking institutional funds" },
  { key: "funding", label: "Funding Cuts", hint: "Withdrawn grants & support" },
];

const TIERS = [
  { c: "#b3122b", label: "Base — Adjuncts / TAs", sub: "Ruby crystal" },
  { c: "#1a5fd0", label: "Middle — Tenure", sub: "Sapphire" },
  { c: "#0f9d68", label: "Middle — Tenure", sub: "Emerald" },
  { c: "#f5c14e", label: "Apex — Administration", sub: "Quartz & gold" },
];

function StressSlider({ ck }) {
  const value = useStress((s) => s[ck.key]);
  return (
    <div className="af-control" data-testid={`control-${ck.key}`}>
      <div className="af-control-head">
        <span className="af-control-label">{ck.label}</span>
        <span className="af-control-val" data-testid={`value-${ck.key}`}>
          {Math.round(value * 100)}%
        </span>
      </div>
      <Slider
        value={[value * 100]}
        min={0}
        max={100}
        step={1}
        onValueChange={(v) => stressStore.setUser({ [ck.key]: v[0] / 100 })}
        data-testid={`slider-${ck.key}`}
      />
      <span className="af-control-hint">{ck.hint}</span>
    </div>
  );
}

function TourOverlay() {
  const active = useTour((t) => t.active);
  const index = useTour((t) => t.index);
  if (!active || index < 0) return null;
  const step = TOUR_STEPS[index];
  const isLast = index === TOUR_STEPS.length - 1;
  return (
    <div className="af-tour" data-testid="tour-overlay">
      <div className="af-tour-top">
        <span className="af-tour-kicker">
          <Clapperboard size={13} /> Guided Tour · {index + 1}/{TOUR_STEPS.length}
        </span>
        <button className="af-tour-exit" onClick={stopTour} data-testid="tour-exit-btn">
          <X size={14} /> End
        </button>
      </div>
      <h3 className="af-tour-title" data-testid="tour-title">
        {step.title}
      </h3>
      <p className="af-tour-caption" data-testid="tour-caption">
        {step.caption}
      </p>
      <div className="af-tour-dots">
        {TOUR_STEPS.map((_, i) => (
          <span key={i} className={`af-tour-dot ${i === index ? "is-on" : ""}`} />
        ))}
      </div>
      <div className="af-tour-nav">
        <button className="af-tour-btn ghost" onClick={prevStep} disabled={index === 0} data-testid="tour-prev-btn">
          <ChevronLeft size={15} /> Back
        </button>
        <button className="af-tour-btn" onClick={nextStep} data-testid="tour-next-btn">
          {isLast ? "Finish" : "Next"} <SkipForward size={15} />
        </button>
      </div>
    </div>
  );
}

function CrisisMeter() {
  const crisis = useStress((s) => crisisLevel(s));
  const pct = Math.round(crisis * 100);
  const status = crisis < 0.25 ? "STABLE" : crisis < 0.55 ? "STRAINED" : crisis < 0.8 ? "CRITICAL" : "COLLAPSE";
  return (
    <div className="af-crisis" data-testid="crisis-meter">
      <div className="af-crisis-top">
        <span className="af-crisis-label">
          <Activity size={13} /> System Stress
        </span>
        <span className="af-crisis-status" data-status={status}>
          {status} <em className="af-crisis-pct" data-testid="crisis-pct">{pct}%</em>
        </span>
      </div>
      <div className="af-crisis-track">
        <div className="af-crisis-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function HUD({ xrStore }) {
  const [scenarios, setScenarios] = useState([]);
  const [name, setName] = useState("");
  const [muted, setMutedState] = useState(false);
  const [xr, setXr] = useState({ vr: false, ar: false });
  const [playing, setPlaying] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const rafRef = useRef();

  useEffect(() => {
    loadScenarios();
    // detect XR support
    if (navigator.xr) {
      navigator.xr.isSessionSupported?.("immersive-vr").then((v) => setXr((x) => ({ ...x, vr: v })));
      navigator.xr.isSessionSupported?.("immersive-ar").then((v) => setXr((x) => ({ ...x, ar: v })));
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const loadScenarios = async () => {
    try {
      const res = await axios.get(`${API}/scenarios`);
      setScenarios(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const saveScenario = async () => {
    const s = stressStore.getState();
    const payload = { name: name.trim() || `Scenario ${scenarios.length + 1}`, phd: s.phd, budget: s.budget, funding: s.funding };
    try {
      await axios.post(`${API}/scenarios`, payload);
      setName("");
      toast.success("Scenario saved", { description: payload.name });
      loadScenarios();
    } catch (e) {
      toast.error("Could not save scenario");
    }
  };

  const applyScenario = (sc) => {
    stressStore.set({ phd: sc.phd, budget: sc.budget, funding: sc.funding });
    toast(`Loaded “${sc.name}”`);
  };

  const shareScenario = (sc, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/?skipIntro=1&phd=${sc.phd}&budget=${sc.budget}&funding=${sc.funding}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(
        () => toast.success("Share link copied", { description: sc.name }),
        () => toast(url)
      );
    } else {
      toast(url);
    }
  };

  const reset = () => {
    stopCollapse();
    stressStore.set({ phd: 0.15, budget: 0.1, funding: 0.1 });
  };

  const stopCollapse = () => {
    cancelAnimationFrame(rafRef.current);
    setPlaying(false);
  };

  const playCollapse = () => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = { ...stressStore.getState() };
    const userStart = stressStore.userTick();
    const dur = 14000; // slow slide from current state into full collapse
    setPlaying(true);
    const step = (now) => {
      // bail out if the user grabs a slider mid-sequence
      if (stressStore.userTick() !== userStart) {
        setPlaying(false);
        return;
      }
      const t = Math.min(1, (now - start) / dur);
      const e = t * t; // ease-in: accelerates toward the brink
      stressStore.set({
        phd: from.phd + (1 - from.phd) * e,
        budget: from.budget + (1 - from.budget) * e,
        funding: from.funding + (1 - from.funding) * e,
      });
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else setPlaying(false);
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const deleteScenario = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/scenarios/${id}`);
      loadScenarios();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleMute = () => {
    if (!stressStore.get("audioReady")) {
      startAudio();
      stressStore.set({ audioReady: true });
      setMutedState(false);
      return;
    }
    const next = !isMuted();
    setMuted(next);
    setMutedState(next);
  };

  return (
    <>
      {/* Title */}
      <div className="af-title" data-testid="app-title">
        <h1>Academic Feudalism</h1>
        <p>A Spatial Study of the Higher-Education Labor Crisis</p>
      </div>

      {/* XR + audio bar (moves to a single bottom row on mobile) */}
      <div className="af-topbar">
        <Button className="af-xr-btn af-tour-start" data-testid="start-tour-btn" onClick={startTour}>
          <Clapperboard size={16} /> Guided Tour
        </Button>
        <Button
          className="af-xr-btn"
          data-testid="enter-vr-btn"
          onClick={() => xrStore.enterVR()}
        >
          <Headset size={16} /> Enter VR
        </Button>
        <Button
          className="af-xr-btn"
          data-testid="enter-ar-btn"
          onClick={() => xrStore.enterAR()}
        >
          <Glasses size={16} /> Enter AR
        </Button>
        <Button className="af-icon-btn" data-testid="audio-toggle" onClick={toggleMute} title="Ambient audio">
          {!stressStore.get("audioReady") || muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </Button>
      </div>

      {/* Mobile-only floating toggle to reveal the controls overlay */}
      <button
        className="af-mobile-toggle"
        data-testid="mobile-hud-toggle"
        onClick={() => setMobileOpen(true)}
        aria-label="Open controls"
      >
        <SlidersHorizontal size={20} />
        <span>Controls</span>
      </button>

      {/* Panels: normal absolute layout on desktop; full-screen overlay on mobile */}
      <div className={`af-panels ${mobileOpen ? "is-open" : ""}`} data-testid="hud-panels">
        <div className="af-panels-mobilebar">
          <span className="af-panels-mobilebar-title">Controls & Scenarios</span>
          <button
            className="af-mobile-close"
            data-testid="mobile-hud-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Close controls"
          >
            <X size={18} /> Close
          </button>
        </div>

        {/* Controls panel */}
        <div className="af-panel af-controls" data-testid="stress-panel">
        <div className="af-panel-head">
          <span>Stress Controls</span>
          <button className="af-reset" onClick={reset} data-testid="reset-btn">
            <RotateCcw size={12} /> Reset
          </button>
        </div>
        {CONTROLS.map((ck) => (
          <StressSlider key={ck.key} ck={ck} />
        ))}
        <CrisisMeter />

        <button
          className={`af-collapse-btn ${playing ? "is-playing" : ""}`}
          onClick={playing ? stopCollapse : playCollapse}
          data-testid="collapse-play-btn"
        >
          {playing ? <Pause size={15} /> : <Play size={15} />}
          {playing ? "Pause Collapse" : "Play Collapse Sequence"}
        </button>
      </div>

      {/* Scenarios panel */}
      <div className="af-panel af-scenarios" data-testid="scenarios-panel">
        <div className="af-panel-head">
          <span>Scenarios</span>
        </div>
        <div className="af-save-row">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name this configuration…"
            className="af-input"
            data-testid="scenario-name-input"
          />
          <Button className="af-save-btn" onClick={saveScenario} data-testid="save-scenario-btn">
            <Save size={15} />
          </Button>
        </div>
        <div className="af-scenario-list" data-testid="scenario-list">
          {scenarios.length === 0 && <span className="af-empty">No saved scenarios yet.</span>}
          {scenarios.map((sc) => (
            <div
              key={sc.id}
              className="af-scenario-item"
              onClick={() => applyScenario(sc)}
              data-testid={`scenario-item-${sc.id}`}
            >
              <ChevronRight size={13} className="af-sc-arrow" />
              <span className="af-sc-name">{sc.name}</span>
              <span className="af-sc-vals">
                {Math.round(sc.phd * 100)}·{Math.round(sc.budget * 100)}·{Math.round(sc.funding * 100)}
              </span>
              <button
                className="af-sc-share"
                onClick={(e) => shareScenario(sc, e)}
                data-testid={`share-scenario-${sc.id}`}
                title="Copy share link"
              >
                <Link2 size={13} />
              </button>
              <button
                className="af-sc-del"
                onClick={(e) => deleteScenario(sc.id, e)}
                data-testid={`delete-scenario-${sc.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Tier legend (bottom-right on tablets, hidden on phones) */}
      <div className="af-legend" data-testid="tier-legend">
        {TIERS.map((t, i) => (
          <div className="af-legend-row" key={i}>
            <span className="af-dot" style={{ background: t.c }} />
            <span className="af-legend-label">{t.label}</span>
            <span className="af-legend-sub">{t.sub}</span>
          </div>
        ))}
      </div>

      <TourOverlay />
    </>
  );
}
